/**
 * Seletor rápido (Ctrl+K): pula para qualquer canal, sala de voz ou conversa
 * direta sem tirar a mão do teclado.
 */
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { C, FONT_MONO, hexA, initialOf, statusColor } from "@/theme";
import { HashIcon, SpeakerIcon } from "@/components/icons";
import { colorOf, useApp } from "@/state/store";

type Alvo = { kind: "canal" | "voz" | "dm"; id: string };

type Item = {
  chave: string;
  alvo: Alvo;
  nome: string;
  /** Texto de apoio à direita: tópico do canal, handle do membro. */
  detalhe: string;
  /** Rótulo curto do tipo, em mono. */
  tipo: string;
  /** Termos usados na filtragem. */
  busca: string;
};

/** Ignora acento e caixa, para "joao" achar "João". */
function normalizar(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

export function QuickSwitcher(): ReactElement | null {
  const { state, actions } = useApp();
  const [termo, setTermo] = useState("");
  const [indice, setIndice] = useState(0);
  const campoRef = useRef<HTMLInputElement>(null);
  const listaRef = useRef<HTMLDivElement>(null);

  const itens = useMemo<Item[]>(() => {
    const canais: Item[] = state.channels.map(canal => ({
      chave: `canal:${canal.id}`,
      alvo: { kind: "canal", id: canal.id },
      nome: `#${canal.name}`,
      detalhe: canal.topic,
      tipo: "CANAL",
      busca: normalizar(`${canal.name} ${canal.topic}`),
    }));
    const salas: Item[] = state.voiceChannels.map(sala => ({
      chave: `voz:${sala.id}`,
      alvo: { kind: "voz", id: sala.id },
      nome: sala.name,
      detalhe: `${state.voiceStates.filter(item => item.voiceId === sala.id).length} na sala`,
      tipo: "VOZ",
      busca: normalizar(sala.name),
    }));
    const membros: Item[] = state.users
      .filter(user => user.id !== state.me?.id)
      .map(user => ({
        chave: `dm:${user.id}`,
        alvo: { kind: "dm", id: user.id },
        nome: user.name,
        detalhe: `@${user.handle}`,
        tipo: "DIRETA",
        busca: normalizar(`${user.name} ${user.handle}`),
      }));
    return [...canais, ...salas, ...membros];
  }, [state.channels, state.me?.id, state.users, state.voiceChannels, state.voiceStates]);

  const filtrados = useMemo(() => {
    const alvo = normalizar(termo.trim());
    if (!alvo) return itens.slice(0, 12);
    return itens.filter(item => item.busca.includes(alvo)).slice(0, 12);
  }, [itens, termo]);

  useEffect(() => {
    if (!state.switcherOpen) return;
    setTermo("");
    setIndice(0);
    // O foco só existe depois que o modal entra no DOM.
    const id = window.setTimeout(() => campoRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [state.switcherOpen]);

  useEffect(() => {
    setIndice(atual => (atual >= filtrados.length ? 0 : atual));
  }, [filtrados.length]);

  // Mantém o item destacado visível quando a navegação é por teclado.
  useEffect(() => {
    const lista = listaRef.current;
    const item = lista?.children[indice];
    if (item instanceof HTMLElement) item.scrollIntoView({ block: "nearest" });
  }, [indice]);

  if (!state.switcherOpen) return null;

  const escolher = (item: Item | undefined): void => {
    if (!item) {
      actions.closeSwitcher();
      return;
    }
    actions.gotoTarget(item.alvo);
    actions.closeSwitcher();
  };

  const aoTeclar = (evento: React.KeyboardEvent<HTMLInputElement>): void => {
    if (evento.key === "ArrowDown") {
      evento.preventDefault();
      setIndice(atual => (filtrados.length === 0 ? 0 : (atual + 1) % filtrados.length));
      return;
    }
    if (evento.key === "ArrowUp") {
      evento.preventDefault();
      setIndice(atual =>
        filtrados.length === 0 ? 0 : (atual - 1 + filtrados.length) % filtrados.length,
      );
      return;
    }
    if (evento.key === "Enter") {
      evento.preventDefault();
      escolher(filtrados[indice]);
      return;
    }
    if (evento.key === "Escape") {
      evento.preventDefault();
      actions.closeSwitcher();
    }
  };

  return (
    <div
      onClick={actions.closeSwitcher}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 80,
        display: "grid",
        placeItems: "start center",
        paddingTop: "14vh",
        background: "rgba(0,0,0,.62)",
        animation: "fadein 150ms ease",
      }}
    >
      <div
        onClick={evento => evento.stopPropagation()}
        style={{
          width: "min(560px, 92vw)",
          border: `1px solid ${C.lineStrong}`,
          borderRadius: 16,
          overflow: "hidden",
          background: "#1d1d1d",
          animation: "pop 200ms ease",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "14px 16px",
            borderBottom: `1px solid ${C.line}`,
          }}
        >
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke={C.textFaint}
            strokeWidth="1.7"
            strokeLinecap="round"
          >
            <circle cx="11" cy="11" r="6.5" />
            <path d="M16 16l4 4" />
          </svg>
          <input
            ref={campoRef}
            value={termo}
            onChange={evento => {
              setTermo(evento.target.value);
              setIndice(0);
            }}
            onKeyDown={aoTeclar}
            placeholder="Ir para canal, sala ou pessoa"
            style={{
              flex: 1,
              minWidth: 0,
              border: 0,
              outline: 0,
              background: "transparent",
              color: C.text,
              fontSize: 15,
            }}
          />
          <span
            style={{
              fontFamily: FONT_MONO,
              fontSize: 9.5,
              letterSpacing: ".1em",
              color: C.textGhost,
              border: `1px solid ${C.lineStrong}`,
              borderRadius: 6,
              padding: "3px 7px",
            }}
          >
            CTRL K
          </span>
        </div>

        <div ref={listaRef} style={{ maxHeight: 380, overflowY: "auto", padding: 6 }}>
          {filtrados.length === 0 ? (
            <div
              style={{
                padding: "26px 14px",
                textAlign: "center",
                fontSize: 13.5,
                color: C.textDim,
              }}
            >
              Nada com esse nome.
              <div style={{ marginTop: 4, fontFamily: FONT_MONO, fontSize: 10.5, color: C.textGhost }}>
                TENTE O NOME DE UM CANAL OU DE UM MEMBRO
              </div>
            </div>
          ) : (
            filtrados.map((item, posicao) => {
              const ativo = posicao === indice;
              const membro =
                item.alvo.kind === "dm"
                  ? state.users.find(user => user.id === item.alvo.id)
                  : undefined;
              const cor = membro ? colorOf(state, membro.id) : C.textFaint;
              return (
                <button
                  key={item.chave}
                  onClick={() => escolher(item)}
                  onMouseEnter={() => setIndice(posicao)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 11,
                    padding: "9px 10px",
                    margin: "1px 0",
                    border: 0,
                    borderRadius: 9,
                    cursor: "pointer",
                    textAlign: "left",
                    background: ativo ? C.selected : "transparent",
                    color: ativo ? C.text : C.textStrong,
                  }}
                >
                  {membro ? (
                    <span style={{ position: "relative", width: 26, height: 26, flex: "0 0 auto" }}>
                      {membro.avatarUrl ? (
                        <img
                          src={membro.avatarUrl}
                          alt=""
                          style={{
                            width: 26,
                            height: 26,
                            borderRadius: "50%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      ) : (
                        <span
                          style={{
                            width: 26,
                            height: 26,
                            display: "grid",
                            placeItems: "center",
                            borderRadius: "50%",
                            fontSize: 11,
                            fontWeight: 600,
                            background: hexA(cor, 0.18),
                            color: cor,
                          }}
                        >
                          {initialOf(membro.name)}
                        </span>
                      )}
                      <span
                        style={{
                          position: "absolute",
                          right: -1,
                          bottom: -1,
                          width: 9,
                          height: 9,
                          borderRadius: 999,
                          border: "2px solid #1d1d1d",
                          background: statusColor(membro.status),
                        }}
                      />
                    </span>
                  ) : (
                    <span
                      style={{
                        width: 26,
                        height: 26,
                        flex: "0 0 auto",
                        display: "grid",
                        placeItems: "center",
                        color: C.textFaint,
                      }}
                    >
                      {item.alvo.kind === "canal" ? (
                        <HashIcon size={16} />
                      ) : (
                        <SpeakerIcon size={16} />
                      )}
                    </span>
                  )}

                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: "block",
                        fontSize: 14,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.nome}
                    </span>
                    {item.detalhe ? (
                      <span
                        style={{
                          display: "block",
                          marginTop: 1,
                          fontSize: 11.5,
                          color: C.textDim,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.detalhe}
                      </span>
                    ) : null}
                  </span>

                  <span
                    style={{
                      fontFamily: FONT_MONO,
                      fontSize: 9,
                      letterSpacing: ".12em",
                      color: C.textGhost,
                      flex: "0 0 auto",
                    }}
                  >
                    {item.tipo}
                  </span>
                </button>
              );
            })
          )}
        </div>

        <div
          style={{
            display: "flex",
            gap: 14,
            padding: "9px 14px",
            borderTop: `1px solid ${C.line}`,
            fontFamily: FONT_MONO,
            fontSize: 9.5,
            letterSpacing: ".1em",
            color: C.textGhost,
          }}
        >
          <span>↑↓ NAVEGAR</span>
          <span>ENTER ABRIR</span>
          <span>ESC FECHAR</span>
        </div>
      </div>
    </div>
  );
}
